import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  FileText,
  User,
  Briefcase,
  Users,
  Compass,
  CheckCircle,
  UserCircle,
  ArrowRight,
  X,
  Info,
  Link2,
  Search,
  Loader2,
  Mic,
} from 'lucide-react'
import type { LucideProps } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { InterviewTypeId, InterviewTypeConfig } from '@/types/interviewer'
import { BUILT_IN_INTERVIEW_TYPES, getDefaultInterviewType } from '@/types/interviewer'
import { usePersons } from '@/hooks/usePersons'
import { LiveRecordingPanel } from './LiveRecordingPanel'

/** Input mode for interview content */
type InputMode = 'paste' | 'record'

// Helper to get person display name
const getPersonDisplayName = (person: { firstName: string; lastName: string }) =>
  `${person.firstName} ${person.lastName}`.trim()

// Icon mapping for interview types
const ICON_MAP: Record<string, React.ComponentType<LucideProps>> = {
  FileText,
  User,
  Briefcase,
  Users,
  Compass,
  CheckCircle,
  UserCircle,
}

interface InterviewSetupProps {
  onSubmit: (data: {
    interviewTypeId: InterviewTypeId
    title: string
    content: string
    context: string
    linkedPersonId?: string
    linkedPersonName?: string
  }) => void
  onCancel: () => void
}

export function InterviewSetup({ onSubmit, onCancel }: InterviewSetupProps) {
  const defaultType = getDefaultInterviewType()
  const [selectedType, setSelectedType] = useState<InterviewTypeConfig>(defaultType)
  const [title, setTitle] = useState('')
  const [context, setContext] = useState('')
  const [content, setContent] = useState('')
  const [linkedPersonId, setLinkedPersonId] = useState<string | undefined>()
  const [linkedPersonName, setLinkedPersonName] = useState<string | undefined>()
  const [personSearch, setPersonSearch] = useState('')
  const [showPersonDropdown, setShowPersonDropdown] = useState(false)
  const [inputMode, setInputMode] = useState<InputMode>('paste')

  // Fetch persons for linking
  const { data: persons, isLoading: personsLoading } = usePersons()

  // Filter persons by search
  const filteredPersons = persons?.filter(
    (p) =>
      getPersonDisplayName(p).toLowerCase().includes(personSearch.toLowerCase()) ||
      p.email?.toLowerCase().includes(personSearch.toLowerCase())
  ) ?? []

  // Reset person linking when type changes
  useEffect(() => {
    if (!selectedType.linksToPersonEntity) {
      setLinkedPersonId(undefined)
      setLinkedPersonName(undefined)
    }
  }, [selectedType])

  const handleSelectPerson = (personId: string, personName: string) => {
    setLinkedPersonId(personId)
    setLinkedPersonName(personName)
    setShowPersonDropdown(false)
    setPersonSearch('')
  }

  const handleClearPerson = () => {
    setLinkedPersonId(undefined)
    setLinkedPersonName(undefined)
  }

  const handleRecordingComplete = (transcript: string) => {
    setContent(transcript)
    setInputMode('paste') // Switch back to paste mode to show the transcript
  }

  const handleSubmit = () => {
    if (!content.trim()) return

    const finalTitle = title.trim() || `${selectedType.label} - ${new Date().toLocaleDateString()}`

    onSubmit({
      interviewTypeId: selectedType.id,
      title: finalTitle,
      content: content.trim(),
      context: context.trim(),
      linkedPersonId,
      linkedPersonName,
    })
  }

  const isValid = content.trim().length > 50

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">New Interview</h2>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
        {/* Left: Type selection and form */}
        <div className="flex-1 flex flex-col gap-4 overflow-auto">
          {/* Interview Type Selection */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Interview Type
            </label>
            <div className="grid grid-cols-4 gap-2">
              {BUILT_IN_INTERVIEW_TYPES.map((type) => {
                const Icon = ICON_MAP[type.icon] || FileText
                const isSelected = selectedType.id === type.id
                return (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type)}
                    className={cn(
                      'relative flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-center',
                      isSelected
                        ? 'border-solution bg-solution/5'
                        : 'border-warm-200 hover:border-warm-300 bg-background'
                    )}
                  >
                    {type.isDefault && (
                      <Badge
                        variant="outline"
                        className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0 bg-background border-solution text-solution"
                      >
                        DEFAULT
                      </Badge>
                    )}
                    {type.linksToPersonEntity && (
                      <Badge
                        variant="outline"
                        className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] px-1.5 py-0 bg-background border-business text-business"
                      >
                        PERSON
                      </Badge>
                    )}
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${type.color}20` }}
                    >
                      <Icon className="w-4 h-4" style={{ color: type.color }} />
                    </div>
                    <span className={cn(
                      'text-xs font-medium',
                      isSelected ? 'text-solution' : 'text-text'
                    )}>
                      {type.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Person Linking (conditional) */}
          {selectedType.linksToPersonEntity && (
            <div>
              <label className="text-sm font-medium mb-1.5 flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5" />
                Link to Person
                <span className="text-text-muted font-normal">(optional)</span>
              </label>
              <div className="relative">
                {linkedPersonId ? (
                  <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-warm-300 bg-background">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-business" />
                      <span className="text-sm">{linkedPersonName}</span>
                    </div>
                    <button
                      onClick={handleClearPerson}
                      className="text-text-muted hover:text-text"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                      <input
                        type="text"
                        value={personSearch}
                        onChange={(e) => {
                          setPersonSearch(e.target.value)
                          setShowPersonDropdown(true)
                        }}
                        onFocus={() => setShowPersonDropdown(true)}
                        placeholder="Search for a person to link..."
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-warm-300 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-solution/30"
                      />
                    </div>
                    {showPersonDropdown && (
                      <Card className="absolute z-10 mt-1 w-full max-h-48 overflow-auto shadow-lg">
                        {personsLoading ? (
                          <div className="flex items-center justify-center p-4">
                            <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
                          </div>
                        ) : filteredPersons.length === 0 ? (
                          <div className="p-4 text-center text-sm text-text-muted">
                            {personSearch ? 'No persons found' : 'Type to search...'}
                          </div>
                        ) : (
                          <div className="py-1">
                            {filteredPersons.slice(0, 10).map((person) => (
                              <button
                                key={person.id}
                                onClick={() => handleSelectPerson(person.id, getPersonDisplayName(person))}
                                className="w-full px-3 py-2 text-left hover:bg-warm-100 flex items-center gap-2"
                              >
                                <User className="w-4 h-4 text-text-muted" />
                                <div>
                                  <div className="text-sm font-medium">{getPersonDisplayName(person)}</div>
                                  {person.email && (
                                    <div className="text-xs text-text-muted">{person.email}</div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </Card>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Title and Context */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">
                Title <span className="text-text-muted font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`${selectedType.label} - ${new Date().toLocaleDateString()}`}
                className="w-full px-3 py-2 rounded-lg border border-warm-300 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-solution/30"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                <Info className="w-3 h-3 inline mr-1" />
                Context <span className="text-text-muted font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="e.g., Beta user, Enterprise segment..."
                className="w-full px-3 py-2 rounded-lg border border-warm-300 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-solution/30"
              />
            </div>
          </div>

          {/* Content - Mode Toggle and Input */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Input Mode Toggle */}
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium flex items-center gap-2">
                {inputMode === 'paste' ? (
                  <FileText className="w-3 h-3" />
                ) : (
                  <Mic className="w-3 h-3" />
                )}
                Interview Content
                <span className="text-problem">*</span>
              </label>

              <div className="flex gap-1 p-0.5 bg-warm-100 rounded-lg">
                <button
                  onClick={() => setInputMode('paste')}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                    inputMode === 'paste'
                      ? 'bg-background text-text shadow-sm'
                      : 'text-text-muted hover:text-text'
                  )}
                >
                  <FileText className="w-3.5 h-3.5" />
                  Paste
                </button>
                <button
                  onClick={() => setInputMode('record')}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                    inputMode === 'record'
                      ? 'bg-background text-text shadow-sm'
                      : 'text-text-muted hover:text-text'
                  )}
                >
                  <Mic className="w-3.5 h-3.5" />
                  Record
                </button>
              </div>
            </div>

            {/* Conditional Content Area */}
            {inputMode === 'paste' ? (
              <>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={selectedType.placeholder}
                  className="flex-1 w-full px-3 py-2 rounded-lg border border-warm-300 bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-solution/30 min-h-[200px]"
                />
                <div className="flex justify-between mt-1 text-xs text-text-muted">
                  <span>
                    {content.length} characters
                    {content.length < 50 && content.length > 0 && (
                      <span className="text-warning ml-2">(minimum 50 characters)</span>
                    )}
                  </span>
                  <span>Paste or type your transcript</span>
                </div>
              </>
            ) : (
              <LiveRecordingPanel
                onComplete={handleRecordingComplete}
                onCancel={() => setInputMode('paste')}
                minLength={50}
              />
            )}
          </div>
        </div>

        {/* Right: Tips panel */}
        <Card className="w-64 p-4 bg-warm-50 flex-shrink-0 h-fit">
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${selectedType.color}20` }}
            >
              {(() => {
                const Icon = ICON_MAP[selectedType.icon] || FileText
                return <Icon className="w-4 h-4" style={{ color: selectedType.color }} />
              })()}
            </div>
            <span className="font-medium text-sm">{selectedType.label}</span>
          </div>

          <p className="text-xs text-text-muted mb-4">{selectedType.description}</p>

          <div className="space-y-2">
            <h4 className="text-xs font-medium">Tips for better analysis:</h4>
            <ul className="space-y-1">
              {selectedType.tips.map((tip, i) => (
                <li key={i} className="text-xs text-text-secondary flex gap-2">
                  <span className="text-solution">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          {selectedType.linksToPersonEntity && (
            <div className="mt-4 pt-4 border-t border-warm-200">
              <div className="flex items-center gap-1.5 text-xs text-business">
                <Link2 className="w-3 h-3" />
                <span className="font-medium">Person Linking Available</span>
              </div>
              <p className="text-xs text-text-muted mt-1">
                Link this interview to a Person entity for better tracking and insights.
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 pt-2 border-t border-warm-200">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isValid}
          className="gap-2"
        >
          Analyze Interview
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
